/**
 * Button Component
 * Telegram native styled button
 */

import React from 'react';
import styled from 'styled-components';
import { hapticFeedback } from '../utils/telegram';

interface ButtonStyledProps {
  variant?: 'primary' | 'secondary' | 'outline' | 'danger';
  size?: 'small' | 'medium' | 'large';
  fullWidth?: boolean;
}

const StyledButton = styled.button<ButtonStyledProps>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-sm);
  padding: ${({ size }) => {
    switch (size) {
      case 'small':
        return 'var(--spacing-sm) var(--spacing-md)';
      case 'large':
        return 'var(--spacing-lg) var(--spacing-xl)';
      default:
        return 'var(--spacing-md) var(--spacing-lg)';
    }
  }};
  width: ${({ fullWidth }) => (fullWidth ? '100%' : 'auto')};
  font-size: ${({ size }) => {
    switch (size) {
      case 'small':
        return 'var(--font-sm)';
      case 'large':
        return 'var(--font-lg)';
      default:
        return 'var(--font-md)';
    }
  }};
  font-weight: var(--font-semibold);
  border-radius: var(--radius-lg);
  border: none;
  transition: all var(--transition);
  cursor: pointer;

  ${({ variant }) => {
    switch (variant) {
      case 'secondary':
        return `
          background: var(--bg-secondary);
          color: var(--text-primary);
          border: 1px solid var(--border-color);

          &:hover:not(:disabled) {
            opacity: 0.9;
          }
        `;
      case 'outline':
        return `
          background: transparent;
          color: var(--accent-green);
          border: 2px solid var(--accent-green);

          &:hover:not(:disabled) {
            background: var(--accent-green);
            color: var(--text-primary);
          }
        `;
      case 'danger':
        return `
          background: var(--accent-red);
          color: #FFFFFF;

          &:hover:not(:disabled) {
            opacity: 0.9;
          }
        `;
      default:
        return `
          background: var(--accent-green);
          color: var(--text-primary);

          &:hover:not(:disabled) {
            opacity: 0.9;
          }
        `;
    }
  }}

  &:active:not(:disabled) {
    transform: scale(0.98);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const LoadingSpinner = styled.span`
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid currentColor;
  border-top-color: transparent;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

interface ButtonProps extends ButtonStyledProps {
  children: React.ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  loading?: boolean;
  type?: 'button' | 'submit' | 'reset';
  icon?: React.ReactNode;
  haptic?: 'light' | 'medium' | 'heavy';
  style?: React.CSSProperties;
  className?: string;
}

const Button: React.FC<ButtonProps> = ({
  children,
  onClick,
  disabled = false,
  loading = false,
  type = 'button',
  variant = 'primary',
  size = 'medium',
  fullWidth = false,
  icon,
  haptic = 'light',
  style,
  className
}) => {
  const handleClick = () => {
    if (!disabled && !loading && onClick) {
      hapticFeedback(haptic);
      onClick();
    }
  };

  return (
    <StyledButton
      type={type}
      onClick={handleClick}
      disabled={disabled || loading}
      variant={variant}
      size={size}
      fullWidth={fullWidth}
      style={style}
      className={className}
    >
      {loading ? (
        <>
          <LoadingSpinner />
          Loading...
        </>
      ) : (
        <>
          {icon && icon}
          {children}
        </>
      )}
    </StyledButton>
  );
};

export default Button;
